import { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { BookOpenText, Sparkle, Broadcast, Trash, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function Stories() {
  const [stories, setStories] = useState(null);
  const [city, setCity] = useState("");
  const [gen, setGen] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/stories").then((r) => setStories(r.data)).catch(() => setStories([]));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!city.trim()) return;
    setGen(true);
    try {
      const { data } = await api.post("/stories/generate", { city: city.trim() });
      toast.success("Story drafted");
      nav(`/stories/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate story");
    }
    setGen(false);
  };

  const publish = async (id) => { await api.post(`/stories/${id}/publish`); load(); toast.success("Story published"); };
  const del = async (id) => { await api.delete(`/stories/${id}`); load(); toast.success("Story deleted"); };

  return (
    <div className="mx-auto max-w-[1400px]">
      <p className="overline text-[#F27A54]">Auto-drafted narratives</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Location Stories</h1>
      <p className="mt-1 text-sm text-muted-foreground">Generate a shareable story from photos in a place. Drafts stay private until you publish.</p>

      <Card className="mt-5 rounded-md border border-border p-5 shadow-none">
        <div className="flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-[#F27A54]" /><h3 className="font-display text-sm font-bold">Draft a new story</h3></div>
        <div className="mt-3 flex gap-2">
          <Input data-testid="story-city-input" value={city} onChange={(e) => setCity(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && generate()}
            placeholder="Enter a city with photos, e.g. Kochi" className="h-10 max-w-sm rounded-md" />
          <Button data-testid="story-generate-btn" onClick={generate} disabled={gen} className="h-10 gap-1.5 rounded-md bg-[#F27A54] hover:bg-[#d9633f]">
            <BookOpenText size={16} weight="bold" />{gen ? "Drafting…" : "Generate Story"}
          </Button>
        </div>
      </Card>

      {stories === null ? (
        <p className="mt-8 text-muted-foreground">Loading…</p>
      ) : stories.length === 0 ? (
        <div className="mt-16 grid place-items-center text-center">
          <BookOpenText size={44} weight="thin" className="text-zinc-300" />
          <p className="mt-3 font-display font-bold">No stories yet</p>
          <p className="text-sm text-muted-foreground">Generate one above or from a trip cluster.</p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stories.map((s) => (
            <Card key={s.id} className="group overflow-hidden rounded-md border border-border shadow-none transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm">
              <Link to={`/stories/${s.id}`} className="block">
                <div className="relative aspect-[16/9] overflow-hidden bg-zinc-100">
                  <img src={imageUrl(s.cover_photo_id)} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className={`absolute right-2 top-2 rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase text-white ${s.status === "published" ? "bg-[#F27A54]" : "bg-amber-500"}`}>{s.status}</span>
                </div>
              </Link>
              <div className="p-4">
                <Link to={`/stories/${s.id}`}><h3 className="font-display font-bold leading-tight hover:text-[#F27A54]">{s.title}</h3></Link>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12} weight="fill" />{[s.city, s.country].filter(Boolean).join(", ")} · {s.photo_ids?.length} photos</p>
                <div className="mt-3 flex gap-2">
                  {s.status !== "published" && <Button size="sm" onClick={() => publish(s.id)} className="h-8 gap-1 rounded-md bg-[#F27A54] hover:bg-[#d9633f]" data-testid={`publish-story-${s.id}`}><Broadcast size={13} weight="bold" />Publish</Button>}
                  <Button size="sm" variant="outline" onClick={() => del(s.id)} className="h-8 gap-1 rounded-md border-red-200 text-red-600 hover:bg-red-50"><Trash size={13} />Delete</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
